import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, ArrowRight, TrendingUp, TrendingDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCrmOverview, getCrmUsageDetail } from '../../../api/kpi';
import { PeriodSelector, useAnalyticsPeriod } from './AnalyticsPeriod';
import CrmDetailModal from './CrmDetailModal';
import {
    CLR, ENGAGEMENT, fmtMoney, fmtNumber, initials, pillPalette, scoreTier,
} from './crmAnalyticsUi';

/**
 * Analytics > CRM Analytics > Overview — the management cockpit.
 *
 * Reads the whole chain in one screen, left to right:
 *
 *   usage → activity → relationships → leads → appraisals → listings → revenue
 *
 * Every card drills into the records behind it, so a number is never a dead
 * end. Sales, Commission, Listings and Appraisals come from the same backend
 * rollup as the Agents tab and the Leaderboard, so the three screens cannot
 * quietly disagree.
 */

const formatValue = (card) => {
    switch (card.format) {
        case 'currency': return fmtMoney(card.value);
        case 'percent': return `${card.value}%`;
        case 'ratio': return fmtNumber(card.value);
        default: return fmtNumber(card.value);
    }
};

const Delta = ({ pct }) => {
    if (pct == null) return null;
    const up = pct >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    const color = up ? CLR.green : CLR.red;
    return (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold" style={{ color }}>
            <Icon className="w-3 h-3" />
            {up ? '+' : ''}{pct}%
        </span>
    );
};

const Card = ({ card, onClick }) => (
    <button
        onClick={card.drill ? onClick : undefined}
        disabled={!card.drill}
        className={`text-left bg-white rounded-lg border border-gray-100 shadow-sm p-3.5 w-full ${
            card.drill ? 'cursor-pointer hover:border-gray-300 hover:shadow-md transition' : ''
        }`}
        title={card.note || (card.drill ? 'View records' : undefined)}
    >
        <div className="text-[10.5px] font-medium text-gray-500 uppercase tracking-wide truncate">
            {card.label}
        </div>
        <div className="mt-1 flex items-baseline gap-1.5 flex-wrap">
            <span className="text-xl font-bold text-gray-900 tabular-nums">{formatValue(card)}</span>
            {card.format === 'ratio' && card.secondary != null && (
                <span className="text-[11px] text-gray-400 tabular-nums">/ {fmtNumber(card.secondary)}</span>
            )}
            <Delta pct={card.delta_pct} />
        </div>
        {card.note && (
            <div className="text-[10px] text-gray-400 mt-1 leading-snug line-clamp-2">{card.note}</div>
        )}
    </button>
);

// `reloadToken` — bump from the parent to force a refetch (see AgentUsagePanel).
const CrmOverviewPanel = ({ onOpenAgentUsage, reloadToken = 0 }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState({
        open: false, loading: false, title: '', rows: [], metric: null, agentId: null,
    });
    const loadTokenRef = useRef(0);

    const period = useAnalyticsPeriod();
    const { range } = period;

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        getCrmOverview({ start_date: range.start, end_date: range.end, compare: 1 })
            .then((res) => { if (myToken === loadTokenRef.current) setData(res.data); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('CRM overview load failed:', err);
                toast.error('Failed to load CRM overview');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [range.start, range.end, reloadToken]);

    const runDetail = (metric, title, agentId = null) => {
        setDetail({ open: true, loading: true, title, rows: [], metric, agentId });
        getCrmUsageDetail({ metric, start_date: range.start, end_date: range.end, agent: agentId || undefined })
            .then((res) => setDetail((d) => ({
                ...d,
                loading: false,
                rows: res.data?.results || [],
                total: res.data?.total,
                truncated: res.data?.truncated,
            })))
            .catch((err) => {
                console.error('CRM overview detail failed:', err);
                toast.error('Failed to load records');
                setDetail((d) => ({ ...d, loading: false, rows: [] }));
            });
    };

    // Re-runs the open drill-down scoped to a different agent (or back to "All
    // agents" for null) - lets the Pipeline Value breakdown be split per agent
    // (CG, 2026-08-28) without closing and reopening the modal.
    const changeDetailAgent = (agentId) => {
        const agentName = agentId
            ? ((data?.leaderboard || []).find((a) => String(a.agent_id) === String(agentId))?.agent_name || 'Agent')
            : 'All agents';
        const cardLabel = detail.title.split(' · ').slice(1).join(' · ') || detail.title;
        runDetail(detail.metric, `${agentName} · ${cardLabel}`, agentId);
    };

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-24 text-gray-500 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading CRM overview…
            </div>
        );
    }

    const cards = data?.cards || [];
    const leaderboard = data?.leaderboard || [];
    const totals = data?.totals || {};
    const engagement = totals.engagement || {};
    const agentCount = totals.agents || 0;

    return (
        <div className="space-y-5">
            <PeriodSelector period={period} />

            {/* Headline cards, ordered as the chain reads:
                usage → activity → relationships → leads → listings → revenue. */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-3">
                {cards.map((card) => (
                    <Card
                        key={card.key}
                        card={card}
                        onClick={() => runDetail(card.drill, `All agents · ${card.label}`)}
                    />
                ))}
            </div>

            {/* Engagement split — the "who is actually adopting this" answer at a glance */}
            {agentCount > 0 && (
                <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">CRM adoption</h3>
                            <p className="text-[11px] text-gray-500 mt-0.5">
                                Based on meaningful activity, not logins — someone who signs in once
                                and does nothing does not count as active.
                            </p>
                        </div>
                        {onOpenAgentUsage && (
                            <button
                                onClick={onOpenAgentUsage}
                                className="text-[12px] font-semibold inline-flex items-center gap-1 flex-shrink-0"
                                style={{ color: CLR.aquaDark }}
                            >
                                Agent Usage <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    <div className="flex h-2.5 rounded-full overflow-hidden mb-2.5" style={{ background: CLR.grayLight }}>
                        {['power_user', 'active', 'low_usage', 'inactive'].map((k) => {
                            const n = engagement[k] || 0;
                            if (!n) return null;
                            return (
                                <div
                                    key={k}
                                    style={{ width: `${(n / agentCount) * 100}%`, background: ENGAGEMENT[k].dot }}
                                    title={`${ENGAGEMENT[k].label}: ${n}`}
                                />
                            );
                        })}
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                        {['power_user', 'active', 'low_usage', 'inactive'].map((k) => (
                            <span key={k} className="inline-flex items-center gap-1.5 text-[11.5px]" style={{ color: CLR.gray }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: ENGAGEMENT[k].dot }} />
                                {ENGAGEMENT[k].label}
                                <strong style={{ color: CLR.black }}>{engagement[k] || 0}</strong>
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Utilisation leaderboard */}
            {leaderboard.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-gray-900">CRM utilisation by agent</h3>
                        {onOpenAgentUsage && (
                            <button
                                onClick={onOpenAgentUsage}
                                className="text-[12px] font-semibold inline-flex items-center gap-1"
                                style={{ color: CLR.aquaDark }}
                            >
                                Full breakdown <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {leaderboard.map((a) => {
                            const { bg, fg } = pillPalette[scoreTier(a.utilisation_score)];
                            const status = ENGAGEMENT[a.engagement_status] || ENGAGEMENT.inactive;
                            return (
                                <li key={a.agent_id} className="px-4 py-2.5 flex items-center gap-3">
                                    <span
                                        className="flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0"
                                        style={{ width: 26, height: 26, background: CLR.grayLight, color: CLR.gray }}
                                    >
                                        {initials(a.agent_name)}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-gray-800 truncate">{a.agent_name}</div>
                                        <div className="text-[11px]" style={{ color: CLR.gray }}>
                                            {fmtNumber(a.total_actions)} actions · {a.active_days} active days
                                            · {fmtNumber(a.contacts_touched)} contacts
                                        </div>
                                    </div>
                                    <span
                                        className="hidden sm:inline-flex items-center gap-1.5 rounded-full flex-shrink-0"
                                        style={{ background: status.bg, color: status.fg, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}
                                    >
                                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: status.dot }} />
                                        {status.label}
                                    </span>
                                    <span className="text-sm tabular-nums text-gray-700 w-16 text-right flex-shrink-0">
                                        {fmtMoney(a.commission)}
                                    </span>
                                    <span
                                        className="inline-flex items-center justify-center rounded-lg font-bold tabular-nums flex-shrink-0"
                                        style={{ background: bg, color: fg, minWidth: 42, padding: '3px 8px', fontSize: 13 }}
                                        title={`Score confidence: ${a.score_confidence}`}
                                    >
                                        {a.utilisation_score}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {(data?.meta?.caveats || []).length > 0 && (
                <p className="text-[10.5px] leading-relaxed" style={{ color: '#9a9c9e' }}>
                    {data.meta.caveats.join(' ')}
                </p>
            )}

            <CrmDetailModal
                {...detail}
                agents={detail.metric === 'pipeline_value' ? leaderboard : null}
                onAgentChange={changeDetailAgent}
                onClose={() => setDetail((d) => ({ ...d, open: false }))}
            />
        </div>
    );
};

export default CrmOverviewPanel;
